// ============================================================
// ROSTER IDENTITY - what to do when a character is loaded that is
// already in the fight.
//
// "Load Brother Castiel" is one question when he is not present and a
// different one when he is: do you want a SECOND Castiel, or is this a
// corrected sheet for the one already bleeding? And if it is a
// correction, do his wounds survive it?
//
// Pure decision logic, kept out of the composition root so it can be
// reasoned about and tested on its own.
// ============================================================
import type { Character } from '../character/types';
import type { Combatant } from './types';
import { combatantFromCharacter, emptyCombatState, withoutStanding } from './factories';
import deepClone from '../../utils/deepClone';

export type IdentityChoice = 'new' | 'update-sheet' | 'replace-all';

export interface IdentityOption {
  label: string;
  value: IdentityChoice;
}

/** The combatant already representing this character, if any. */
export function findExisting(roster: Combatant[], character: Character): Combatant | null {
  return roster.find((c) => c.sheet?.id === character.id) ?? null;
}

/**
 * The choices to offer. With no conflict there is only one sensible
 * answer, so callers can skip the dialog entirely when this returns a
 * single option.
 */
export function identityOptions(existing: Combatant | null, character: Character): IdentityOption[] {
  if (!existing) return [{ label: `Add "${character.name}" to the roster`, value: 'new' }];
  return [
    { label: `Add a second "${character.name}"`, value: 'new' },
    { label: `Update ${existing.name}'s sheet, keep wounds`, value: 'update-sheet' },
    { label: `Replace ${existing.name} entirely (reset state)`, value: 'replace-all' },
  ];
}

export interface IdentityResult {
  combatant: Combatant;
  /** true when an existing roster entry was updated in place. */
  replaced: boolean;
}

/**
 * Apply the chosen resolution.
 *
 * An in-place update KEEPS THE COMBATANT ID - same person, so defence
 * documents, turn participants and anyone currently targeting them all
 * keep resolving. Only `replace-all` resets combat state; `update-sheet`
 * is the mid-battle stat correction and leaves wounds alone.
 */
export function resolveIdentity(roster: Combatant[], character: Character, choice: IdentityChoice): IdentityResult {
  const existing = findExisting(roster, character);

  if (existing && (choice === 'update-sheet' || choice === 'replace-all')) {
    return {
      combatant: {
        ...existing,
        sheet: deepClone(withoutStanding(character)),
        name: character.name,
        // A new sheet means new weapon ids, so the old grid arrangement
        // no longer refers to anything.
        slotOrder: undefined,
        state: choice === 'replace-all' ? emptyCombatState(withoutStanding(character)) : existing.state,
        updatedAt: Date.now(),
      },
      replaced: true,
    };
  }

  return { combatant: combatantFromCharacter(character, { order: roster.length }), replaced: false };
}
